import { ComparisonField } from '@equip.health/ui';
import isEqual from 'lodash/isEqual';
import {
  ContactComparisonEntity,
  CoverageComparisonEntity,
  EitherComparisonEntity,
  NormalizeDuplicateResponseData,
} from '~/lib/constants/inquiry.constants';

export type MergedRecord =
  | MergeDuplicateContactsData
  | MergeDuplicateCoveragesData;

export const areRecordsIdentical = (
  a: EitherComparisonEntity,
  b: EitherComparisonEntity,
) => {
  const sortFields = (fields: ComparisonField[]) =>
    [...fields].sort((f1, f2) => f1.label.localeCompare(f2.label));
  return isEqual(sortFields(a.fields), sortFields(b.fields));
};

const getNextMatchId = (
  nextMatch: ContactComparisonEntity | CoverageComparisonEntity,
) => {
  const nextMatchIdKey = [
    'coverageExternalId',
    'supportExternalId',
    'contactExternalId',
  ].find((key) => key in nextMatch && !!nextMatch[key]);

  return nextMatch[nextMatchIdKey];
};

const isDestinationAlreadySelected = (
  mergedRecords: MergedRecord[],
  matchId: string,
) =>
  mergedRecords.some((record) =>
    [
      'destinationContactId',
      'destinationSupportId',
      'destinationCoverageId',
    ].some((key) => key in record && record[key] === matchId),
  );

export const findNextSourceIndex = (
  allDuplicates: NormalizeDuplicateResponseData[],
  allMergedRecords: MergedRecord[],
  currentIdx: number,
): number => {
  if (!allDuplicates?.length) {
    return null;
  }

  let nextIndex = currentIdx + 1;
  // Keep checking for a valid destination until we reach the end of the list
  while (nextIndex < allDuplicates?.length) {
    const nextSource = allDuplicates[nextIndex];
    const nextMatch = nextSource.matchedEntities[0];
    const nextMatchId = getNextMatchId(nextMatch);

    // If the next match is not already selected as a destination, we can return the next index
    if (!isDestinationAlreadySelected(allMergedRecords, nextMatchId)) {
      return nextIndex;
    }
    // Otherwise, we increment the index and check the next one
    nextIndex++;
  }
  // If we reach here, it means we've gone through all duplicates without finding a valid next index. Returning an
  // index >= duplicates.length, we signal that there are no more potential matches to process.
  return nextIndex;
};
